import {
  DestroyRef,
  Injectable,
  WritableSignal,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { Subject, merge, BehaviorSubject, from, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  scan,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import {
  MessagesApiService,
  SendMessageApiResponse,
} from './messagesApi.service';
import { Message } from '../types/message';
import {
  ChatAiStatus,
  isChatAiBusy,
  normalizeChatAiStatus,
  shouldChatAiStatusHideFooter,
} from '../types/chat-ai-status';
import { StoryService } from '../../stories/story.service';
import { SupabaseService } from '../../supabase/supabase.service';
import { ReInfiniteEvent } from '../../virtual-scroll/virtual-scroll-viewport.component';
import { AuthService } from '../../auth/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { generateSeparator } from '../utils/generateSeparator';
import { populateShowUserInfo } from '../utils/populateShowUserInfo';
import { ChatLocalStorageService } from './chat-localstorage.service';
import { randomId } from '../utils/randomId';
import { GalleryPhoto } from '@capacitor/camera';
import { SelectImpersonateService } from './select-impersonate.service';
import { Chat } from '../../stories/types/Chat';
import { PosthogService } from '../../analytics/posthog.service';
import { CreditsService } from '../../credits/credits.service';
import {
  isMasterVisibleMessage,
  isStorySetupMessage,
  isVisibleChatMessage,
  hasGameplayMasterMessage,
  parseStorySetupAnswers,
} from '../utils/story-setup-message.utils';

@Injectable()
export class ChatService {
  id = signal<number>(0);
  destroy = inject(DestroyRef);
  private translate = inject(TranslateService);
  supabase = inject(SupabaseService);
  auth = inject(AuthService);
  story = inject(StoryService, { optional: true });
  messagesApi = inject(MessagesApiService);
  chatStorage = inject(ChatLocalStorageService);

  chats$ = toObservable(this.story?.chats ?? signal(null));
  chatData = toSignal(
    this.chats$.pipe(
      switchMap((chats) => {
        if (chats?.find((el) => el.id === this.id()))
          return of(chats?.find((el) => el.id === this.id()));
        else
          return from(
            this.supabase.client
              .from('Chats')
              .select(
                `id,
                name,
                kind,
                story:Stories (
                  name
                ),
                friendship:friendships (
                  user1:Users!friendshipss_user1_fkey (
                  id,
                    displayName
                  ),
                  user2:Users!friendshipss_user2_fkey (
                  id,
                    displayName
                  )
                ),
                users:Chats_Users (
                  user (
                    id,
                    inc_id,
                    displayName
                  )
                )`
              )
              .eq('id', this.id())
              .then((res) => res.data?.[0] as any as Chat | undefined)
          );
      })
    )
  );
  users = computed(() => this.chatData()?.users.map((u) => u.user) ?? []);
  userIds = computed(() => this.users().map((el) => el.id) ?? []);
  currentUser = toSignal(this.auth.profile$);
  impersonateService = inject(SelectImpersonateService, { optional: true });
  private posthog = inject(PosthogService);
  private credits = inject(CreditsService);
  private masterInvokeStartedAt: number | null = null;

  /** Full message list including hidden setup messages. */
  rawList: WritableSignal<Message[]> = signal([]);
  /** Messages shown in the chat UI (excludes storySetup). */
  list = computed(() => this.rawList().filter(isVisibleChatMessage));
  /** Preferenze inviate: batch chat + cache StoryService (query DB). */
  hasStorySetupMessage = computed(
    () =>
      this.rawList().some(isStorySetupMessage) ||
      (this.story?.hasStorySetupMessage() ?? false),
  );
  hasVisibleMasterMessage = computed(() =>
    this.rawList().some(isMasterVisibleMessage),
  );
  hasGameplayMasterMessage = computed(() =>
    hasGameplayMasterMessage(this.rawList()),
  );
  newLocallist: WritableSignal<Message[]> = signal([]);
  lastLocalMessage = computed(() => {
    return Math.max(
      ...this.rawList()
        .filter((el) => el.id && !(typeof el.id === 'string'))
        .map((el) => +el.id)
    );
  });
  fisrtLocalMessage = computed(() => {
    return Math.min(
      ...this.rawList()
        .filter((el) => el.id && !(typeof el.id === 'string'))
        .map((el) => +el.id)
    );
  });
  lastOpenDate = signal(Date.now());

  direction: WritableSignal<'fromtop' | 'frombottom'> = signal('frombottom');
  startingElement: WritableSignal<string | undefined> = signal(undefined);
  bufferSize: WritableSignal<number> = signal(40);

  trackBy: (a: Message) => string = (el) => {
    return el.id ? el.id + '' : el.localId ?? '';
  };

  /**
   * Chiave dell'ultimo messaggio Narratore visibile; null se la coda della chat
   * non è caricata (apertura su segnalibro): in quel caso nessuno è "l'ultimo".
   */
  latestMasterMessageKey = computed(() => {
    if (!this.bottomReached()) return null;
    const visible = this.list();
    for (let i = visible.length - 1; i >= 0; i--) {
      if (isMasterVisibleMessage(visible[i])) return this.trackBy(visible[i]);
    }
    return null;
  });

  firstBatchLoaded: BehaviorSubject<boolean> = new BehaviorSubject(false);

  topReached: WritableSignal<boolean> = signal(false);
  bottomReached: WritableSignal<boolean> = signal(false);

  aiStatus = signal<ChatAiStatus>(null);
  /** True quando Master/Lore/Story-setting è occupato; blocca nuove invocazioni. */
  masterBusy = computed(() => isChatAiBusy(this.aiStatus()));
  /** True quando il footer di invio messaggio deve essere nascosto (prima fasi AI). */
  masterLoading = computed(() => shouldChatAiStatusHideFooter(this.aiStatus()));

  /**
   * Imposta subito lo stato “in elaborazione” quando l’utente invoca il master.
   * Il server aggiorna `Chats.ai_status` e il realtime può arrivare dopo il 202;
   * senza questo passaggio il footer resta disallineato fino all’evento DB.
   */
  beginMasterInvoke(): void {
    this.masterInvokeStartedAt = Date.now();
    this.setAiStatus('recalling');
  }

  /** Se la POST di invocazione fallisce (rete, 401, …) prima che il flusso server parta. */
  resetMasterInvokeOnClientError(): void {
    this.posthog.action('call_master', 'error', {
      chatId: this.id(),
      storyId: this.story?.id(),
      source: 'client',
    });
    this.masterInvokeStartedAt = null;
    this.setAiStatus(null);
  }

  private setAiStatus(next: ChatAiStatus): void {
    const prev = this.aiStatus();
    this.aiStatus.set(next);
    if (next === 'worldbuilding' || (isChatAiBusy(prev) && next === null)) {
      void this.credits.refreshWallet();
    }
    if (isChatAiBusy(prev) && next === null) {
      this.posthog.action('call_master', 'success', {
        chatId: this.id(),
        storyId: this.story?.id(),
        durationMs: this.masterInvokeStartedAt
          ? Date.now() - this.masterInvokeStartedAt
          : undefined,
      });
    }
    if (!isChatAiBusy(next)) {
      this.masterInvokeStartedAt = null;
    }
  }

  onNewMessage$ = new Subject<Message[]>();
  onNewMessageFromUser$ = new Subject<Message>();
  onUpdateMessage$ = new Subject<Message>();

  newMessageFromUser$: Subject<Message> = new Subject<Message>();
  private readonly realtimeReconnect$ = new Subject<void>();

  public sendNewMessage(
    message: Omit<Message, 'id' | 'userId'>,
    image?: GalleryPhoto
  ) {
    const _localId = randomId();
    let _message: any = {
      ...message,
      chatId: this.id(),
      userId: this.currentUser()?.id,
      localId: _localId,
      type: 'me',
    };
    this.newMessageFromUser$.next({
      ..._message,
      userData: this.currentUser(),
      impersonateData: this.impersonateService?.impersonate(),
    });
    delete _message.showUserInfo;
    delete _message.type;
    delete _message.messageQuoted;
    const analyticsProps = {
      chatId: this.id(),
      storyId: this.story?.id(),
      hasImage: !!image,
      hasQuote: !!message.messageQuoteId,
      impersonateCharacterId: message.impersonate,
      textLength: message.text?.length ?? 0,
    };
    const finalizeSend = tap({
      next: (res: SendMessageApiResponse) => {
        this.confirmSentMessageFromApi(_localId, res);
        this.posthog.action('send_message', 'success', analyticsProps);
      },
      error: () =>
        this.posthog.action('send_message', 'error', analyticsProps),
    });
    if (image)
      return from(this.messagesApi.loadImage(this.id()!, image)).pipe(
        catchError(() => of({ data: null, error: true })),
        switchMap(({ data, error }) =>
          this.messagesApi.sendMessage({
            ..._message,
            image: data?.id ? data?.path : null,
          })
        ),
        finalizeSend,
        take(1)
      );
    return this.messagesApi.sendMessage({ ..._message, image: null }).pipe(
      finalizeSend,
      take(1),
    );
  }

  /** Conferma subito il messaggio ottimistico con id e testo processato dal POST. */
  private confirmSentMessageFromApi(
    localId: string,
    response: SendMessageApiResponse,
  ): void {
    const row = response.data?.[0];
    if (response.error || !row?.id) return;

    let updated: Message | undefined;
    const patch: Partial<Message> = { id: row.id };
    if (row.text != null) patch.text = row.text;
    if (row.createdAt != null) patch.createdAt = row.createdAt;
    if (row.updatedAt != null) patch.updatedAt = row.updatedAt;
    else if (row.createdAt != null) patch.updatedAt = row.createdAt;
    if (row.localId != null) patch.localId = row.localId;

    this.rawList.update((l) => {
      const msgIndex = l.findIndex((el) => el.localId === localId);
      if (msgIndex === -1) return l;
      const newList = [...l];
      newList[msgIndex] = { ...newList[msgIndex], ...patch };
      updated = newList[msgIndex];
      return newList;
    });

    if (!updated) {
      this.newLocallist.update((l) => {
        const msgIndex = l.findIndex((el) => el.localId === localId);
        if (msgIndex === -1) return l;
        const newList = [...l];
        newList[msgIndex] = { ...newList[msgIndex], ...patch };
        updated = newList[msgIndex];
        return newList;
      });
    }

    if (!updated) return;

    this.noteStorySetupIfNeeded(updated);

    this.onUpdateMessage$.next(updated);
    this.chatStorage.setLastMessageIdInStorage(
      this.id(),
      +updated.id,
      updated.createdAt,
    );
  }

  private noteStorySetupIfNeeded(message: Pick<Message, 'meanings' | 'extra_data'>): void {
    if (!isStorySetupMessage(message)) return;
    this.story?.markStorySetupPresent(parseStorySetupAnswers(message.extra_data));
  }

  public loadMoreMessagesTop(ev: ReInfiniteEvent) {
    this.getMessageTop()
      .pipe(
        tap((list) => {
          this.rawList.update((l) => populateShowUserInfo(list).concat(l));
        }),
        tap(() => ev.complete())
      )
      .subscribe();
  }

  public loadMoreMessagesBottom(ev: ReInfiniteEvent) {
    this.getMessageBottom()
      .pipe(
        tap((list) => {
          this.rawList.update((l) => l.concat(populateShowUserInfo(list)));
        }),
        tap((l) => {
          if (l.at(-1)) {
            this.chatStorage.setLastMessageIdInStorage(
              this.id(),
              +l.at(-1)!.id,
              l.at(-1)!.createdAt
            );
          }
        }),
        tap(() => {
          this.handleLocalMessage();
        }),
        tap(() => ev.complete())
      )
      .subscribe();
  }

  private getMessageBottom() {
    return this.messagesApi
      .getMessagesBottom(this.id(), this.lastLocalMessage(), this.bufferSize())
      .pipe(
        map((res) => {
          res.messages = res.messages.sort(
            (a, b) =>
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
          return res;
        }),
        map((data) => {
          data.messages = data.messages.map((message) => {
            return {
              ...message,
              type: message.userId
                ? message.userId === this.currentUser()?.id
                  ? 'me'
                  : 'user'
                : 'system',
              showUserInfo: false,
            };
          });
          return {
            messages: data.messages as unknown as Message[],
            bottom: data.bottom,
          };
        }),
        map(({ messages, bottom }) => {
          this.bottonReachHandler(bottom);
          let newIndex = messages
            .filter((el) => el.type === 'user')
            .findIndex(
              (el) => new Date(el.createdAt).getTime() > this.lastOpenDate()
            );
          if (newIndex != -1) {
            messages.splice(
              newIndex,
              0,
              generateSeparator(
                this.id(),
                this.translate.instant('chat.separator.newMessages'),
              ),
            );
          }
          return messages;
        })
      );
  }

  private getMessageTop() {
    return this.messagesApi
      .getMessagesTop(this.id(), this.fisrtLocalMessage(), this.bufferSize())
      .pipe(
        map((data) => {
          data.messages = data.messages.map((message) => {
            return {
              ...message,
              type: message.userId
                ? message.userId === this.currentUser()?.id
                  ? 'me'
                  : 'user'
                : 'system',
              showUserInfo: false,
            };
          });
          return data;
        }),
        map(({ messages, top }) => {
          this.topReachHandler(top);
          return messages as unknown as Message[];
        })
      );
  }

  public loadListOfMessagesAroundIndex(index: number) {
    this.startingElement.set(index + '');
    return this.messagesApi
      .getMessagesAroundMessage(this.id(), index, this.bufferSize())
      .pipe(
        tap(({ top, bottom }) => {
          this.topReachHandler(top);
          this.bottonReachHandler(bottom);
        }),
        map(({ messages }) => {
          return messages.map((message) => {
            return {
              ...message,
              type: message.userId
                ? message.userId === this.currentUser()?.id
                  ? 'me'
                  : 'user'
                : 'system',
            };
          }) as unknown as Message[];
        }),
        map((messages) => {
          let newIndex = messages
            .filter((el) => el.type === 'user')
            .findIndex(
              (el) => new Date(el.createdAt).getTime() > this.lastOpenDate()
            );
          if (newIndex != -1) {
            messages.splice(
              newIndex,
              0,
              generateSeparator(
                this.id(),
                this.translate.instant('chat.separator.newMessages'),
              ),
            );
          }
          return messages;
        }),
        tap((l) => {
          this.rawList.set(populateShowUserInfo(l));
        }),
        tap((l) => {
          if (l.at(-1)) {
            this.chatStorage.setLastMessageIdInStorage(
              this.id(),
              +l.at(-1)!.id,
              l.at(-1)!.createdAt
            );
          }
        }),
        tap(() => {
          this.handleLocalMessage();
        })
      );
  }

  private handleNewMessageInChat() {
    return this.messagesApi.listenOtherUserMessages(this.id()).pipe(
      filter(() => this.bottomReached()),
      filter((msg) => msg.userId !== this.currentUser()?.id),
      scan((acc, message, i) => {
        let newMessageList: Message[] = [];
        this.rawList.update((l) => {
          newMessageList =
            !l.some((el) => el.meanings?.includes('separator')) && !acc
              ? [
                  generateSeparator(
                    this.id(),
                    this.translate.instant('chat.separator.newMessages'),
                  ),
                  {
                    ...message,
                    type: message.userId
                      ? message.userId === this.currentUser()?.id
                        ? 'me'
                        : 'user'
                      : 'system',
                    showUserInfo: true,
                  },
                ]
              : [
                  {
                    ...message,
                    type: message.userId
                      ? message.userId === this.currentUser()?.id
                        ? 'me'
                        : 'user'
                      : 'system',
                    showUserInfo:
                      l[l.length - 1]?.userId !== message.userId ||
                      l[l.length - 1]?.impersonate !== message.impersonate,
                  },
                ];
          return [...l, ...newMessageList];
        });
        this.onNewMessage$.next(newMessageList);
        if (newMessageList.at(-1)) {
          this.chatStorage.setLastMessageIdInStorage(
            this.id(),
            +newMessageList.at(-1)!.id,
            newMessageList.at(-1)!.createdAt
          );
        }
        return true;
      }, false)
    );
  }

  private handleNewMessageFromUser() {
    return this.newMessageFromUser$.pipe(
      tap((message) => {
        if (this.bottomReached()) {
          this.rawList.update((l) => [
            ...l.filter((el) => el.localId !== message.localId),
            {
              ...message,
              type: message.userId === this.currentUser()?.id ? 'me' : 'user',
              showUserInfo:
                l[l.length - 1]?.userId !== message.userId ||
                l[l.length - 1]?.impersonate !== message.impersonate,
            },
          ]);
          this.onNewMessageFromUser$.next(message);
        } else if (message.localId && !message.id) {
          let _l = this.newLocallist();
          let _message = {
            ...message,
            type: message.userId === this.currentUser()?.id ? 'me' : 'user',
            showUserInfo:
              _l[_l.length - 1]?.userId !== message.userId ||
              _l[_l.length - 1]?.impersonate !== message.impersonate,
          } as Message;
          this.newLocallist.update((l) => [...l, _message]);
          this.onNewMessageFromUser$.next(_message);
        }
      })
    );
  }

  private handleUpdateOnMessage() {
    return this.messagesApi.listenUpdateOtherUserMessages(this.id()).pipe(
      tap((message) => {
        if (this.rawList().find((el) => el.id === message.id)) {
          this.rawList.update((l) => {
            let newList = [...l];
            let msgIndex = newList.findIndex(
              (el) => el.id === message.id || el.localId === message.localId
            );
            newList[msgIndex] = message;
            return newList;
          });
          this.onUpdateMessage$.next(message);
        }
      })
    );
  }

  private handleInsertedMessageFromUser() {
    return this.messagesApi.listenMyMessages(this.id()).pipe(
      tap((message) => {
        this.newMessageFromUser$.next(message);
      }),
      tap((message) => {
        this.chatStorage.setLastMessageIdInStorage(
          this.id(),
          +message.id,
          message.createdAt
        );
      })
    );
  }

  private handleUpdateMessageFromUser() {
    return this.messagesApi.listenUpdateMyMessages(this.id()).pipe(
      filter((message) => {
        let l = this.rawList();
        return l.some(
          (el) => el.id === message.id || el.localId === message.localId
        );
      }),
      tap((message) => {
        if (
          this.rawList().find(
            (el) => el.id === message.id || el.localId === message.localId
          )
        ) {
          let _message = message;
          this.rawList.update((l) => {
            let newList = [...l];
            let msgIndex = newList.findIndex(
              (el) => el.id === message.id || el.localId === message.localId
            );
            newList[msgIndex] = {
              ...message,
              type: message.userId
                ? message.userId === this.currentUser()?.id
                  ? 'me'
                  : 'user'
                : 'system',
              showUserInfo: newList[msgIndex].showUserInfo,
            };
            _message = newList[msgIndex];
            return newList;
          });
          this.onUpdateMessage$.next(_message);
        }
      }),
      tap((message) => {
        this.chatStorage.setLastMessageIdInStorage(
          this.id(),
          +message.id,
          message.createdAt
        );
      })
    );
  }

  bottonReachHandler(val: boolean) {
    this.bottomReached.set(val);
  }
  topReachHandler(val: boolean) {
    this.topReached.set(val);
  }

  handleLocalMessage() {
    if (this.bottomReached()) {
      let _list = this.newLocallist();
      let newMessageList: Message[] = [];
      this.rawList.update((l) => {
        newMessageList = _list.map((message, i) => {
          return {
            ...message,
            showUserInfo: i === 0,
          };
        });
        return [...l, ...newMessageList];
      });
      if (newMessageList.length > 0) {
        this.onNewMessageFromUser$.next(newMessageList[0]);
      }
      this.newLocallist.set([]);
    }
  }

  private listenNewMessages() {
    return merge(
      this.handleNewMessageFromUser(),
      this.handleNewMessageInChat(),
      this.handleUpdateOnMessage(),
      this.handleInsertedMessageFromUser(),
      this.handleUpdateMessageFromUser()
    );
  }

  public init(chat: number, startingMessage?: number) {
    this.id.set(chat);
    let data = this.chatStorage.getLastMessageIdInStorage(chat);
    if (startingMessage) {
      this.startingElement.set(startingMessage + '');
    } else if (data) {
      this.startingElement.set(data.id + '');
      this.lastOpenDate.set(new Date(data.date).getTime());
    }

    void this.refetchAiStatus();
    void this.story?.refreshStorySetupPresence();

    merge(of(undefined), this.realtimeReconnect$)
      .pipe(
        switchMap(() =>
          this.supabase.watchChange<{ ai_status: string | null }>(
            'chat-ai-status-' + chat,
            'UPDATE',
            'Chats',
            'id=eq.' + chat
          )
        ),
        takeUntilDestroyed(this.destroy)
      )
        .subscribe((payload) => this.setAiStatus(normalizeChatAiStatus(payload.ai_status ?? null)));


    this.auth.profile$
      .pipe(
        take(1),
        switchMap(() =>
          this.loadListOfMessagesAroundIndex(
            +(this.startingElement() ?? 0)
          ).pipe(
            tap(() => {
              this.firstBatchLoaded.next(true);
            }),
            take(1)
          )
        ),
        switchMap(() =>
          merge(of(undefined), this.realtimeReconnect$).pipe(
            switchMap(() => this.listenNewMessages())
          )
        ),
        takeUntilDestroyed(this.destroy)
      )
      .subscribe();
  }

  /** Called when the app returns from a long background pause. */
  syncOnForeground(): void {
    const chatId = this.id();
    if (!chatId) return;

    void this.refetchAiStatus();

    this.getLastMessageId()
      .pipe(
        take(1),
        switchMap((last) => {
          if (!last?.id || typeof last.id !== 'number') {
            return of(null);
          }

          const numericIds = this.rawList()
            .filter((el) => el.id && typeof el.id !== 'string')
            .map((el) => +el.id);
          const localMax = numericIds.length ? Math.max(...numericIds) : 0;

          if (last.id <= localMax) {
            return of(null);
          }

          if (this.bottomReached()) {
            return this.loadListOfMessagesAroundIndex(last.id);
          }

          return this.messagesApi
            .getMessagesBottom(chatId, localMax, 100)
            .pipe(
              map((res) => res.messages),
              tap((messages) => {
                if (!messages.length) return;
                const typed = messages.map((message) => ({
                  ...message,
                  type: message.userId
                    ? message.userId === this.currentUser()?.id
                      ? 'me'
                      : 'user'
                    : 'system',
                  showUserInfo: false,
                })) as Message[];
                this.newLocallist.update((l) => {
                  const existingIds = new Set(l.map((m) => m.id));
                  const novel = typed.filter((m) => !existingIds.has(m.id));
                  return [...l, ...populateShowUserInfo(novel)];
                });
              })
            );
        }),
        tap(() => this.realtimeReconnect$.next()),
        catchError(() => {
          this.realtimeReconnect$.next();
          return of(null);
        })
      )
      .subscribe();
  }

  /** Allinea `aiStatus` locale al valore su `Chats.ai_status` (es. dopo 409 idempotenza). */
  syncAiStatusFromServer(): void {
    void this.supabase.client
      .from('Chats')
      .select('ai_status')
      .eq('id', this.id())
      .single()
      .then(({ data: chatRow }) =>
        this.setAiStatus(normalizeChatAiStatus(chatRow?.ai_status ?? null))
      );
  }

  private refetchAiStatus(): void {
    this.syncAiStatusFromServer();
  }

  getMessageByIdFromLocalList(id: number | string) {
    return this.rawList().find((el) => el.id === id || el.localId === id);
  }

  getLastMessageId() {
    return this.messagesApi.getLastMessage(this.id()).pipe(
      map((message) => {
        let localMessage = this.newLocallist().at(-1);
        return localMessage &&
          new Date(localMessage.createdAt).getTime() >=
            new Date(message?.createdAt ?? '').getTime()
          ? localMessage
          : message;
      })
    );
  }

  static factory(chatId: number | null, startingMessage?: number) {
    let chatService = new ChatService();
    if (chatId || chatId === 0) chatService.init(chatId, startingMessage);
    return chatService;
  }
}
